import { handleActions } from 'redux-actions';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import values from 'lodash/values';
import range from 'lodash/range';
import qs from 'qs';

import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';
import createForm from './components/poll-form';

const pollForm = createForm({
	namespace: 'pollsList',
	stateNodeName: 'pollForm',
	formName: 'pollForm'
});

// actions types
const FETCH_DATA_SUCCESS = 'polls-list/fetch-data-success';
const FETCH_DATA_ERROR = 'polls-list/fetch-data-error';

const SORT_BY_COLUMN = 'polls-list/table-sort-by-column';
const GOTO_PAGE_SUCCESS = 'polls-list/goto-page-success';
const TOGGLE_PROGRESS_BAR = 'polls-list/toggle-progress-bar';

const DELETE_POLL_SUCCESS = 'polls-list/delete-poll-success';
const EDIT_POLL = 'polls-list/edit-poll';

// permissions
export const allowedPermissions = {
	view: 'polls_list',
	create: 'polls_create',
	edit: 'polls_create',
	delete: 'polls_delete'
};

// initial state
const initialState = {
	data: null,
	table: {
		columns: {
			id: {
				index: 'id',
				title: 'ID',
				direction: 'desc'
			},
			created_at: {
				index: 'created_at',
				title: 'Created at',
				direction: 'desc'
			},
			url: {
				index: 'form_url',
				title: 'Form URL'
			},
			status: {
				index: 'status',
				title: 'Status',
				direction: 'desc'
			},
			language: {
				index: 'language',
				title: 'Language',
				direction: 'desc'
			},
			actions: {
				title: 'Actions',
				className: 'actions'
			}
		},
		sortByColumn: {
			index: 'created_at',
			title: 'Created at',
			direction: 'desc'
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 50,
		total: null
	},
	pollForm: pollForm.initialState,
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: () => (dispatch, getState) => {
		const { pagination: { perPage } } = getState().pollsList;
		return request(0, perPage, dispatch);
	},

	sortByColumn: columnIndex => ({
		type: SORT_BY_COLUMN,
		payload: columnIndex
	}),

	gotoPage: page => (dispatch, getState) => {
		const { perPage } = getState().pollsList.pagination;
		const offset = (page - 1) * perPage;

		dispatch(actions.toggleProgressBar());

		request(offset, perPage, dispatch).then(action => {
			if (action.type === FETCH_DATA_SUCCESS) {
				dispatch({
					type: GOTO_PAGE_SUCCESS,
					payload: page
				});
			}
		});
	},

	editPoll: row => ({
		type: EDIT_POLL,
		payload: row
	}),

	deletePoll: id => (dispatch, getState) => {
		dispatch(actions.toggleProgressBar());
		http({
			url: `${endpoints.POLLS.BASE}/${id}`,
			method: 'delete',
			actions: [DELETE_POLL_SUCCESS, FETCH_DATA_ERROR]
		})(dispatch);
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	})
}, pollForm.actions);

// reducers
const reducers = Object.assign({
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state,
		data: action.payload.data.rows,
		pagination: {
			...state.pagination,
			total: action.payload.data.total
		},
		showProgressBar: false,
		error: false
	}),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[SORT_BY_COLUMN]: (state, action) => {
		const { sortByColumn, columns } = state.table;
		const columnIndex = action.payload;
		const direction =
			sortByColumn['index'] == columnIndex
				? sortByColumn.direction === 'desc' ? 'asc' : 'desc'
				: columns[columnIndex].direction;
		return {
			...state,
			table: {
				...state.table,
				sortByColumn: {
					...columns[columnIndex],
					direction
				}
			}
		};
	},
	[GOTO_PAGE_SUCCESS]: (state, action) => ({
		...state,
		pagination: {
			...state.pagination,
			currentPage: action.payload
		}
	}),
	[DELETE_POLL_SUCCESS]: (state, action) => ({
		...state,
		data: null,
		showProgressBar: false
	}),
	[EDIT_POLL]: (state, action) => ({
		...state,
		pollForm: {
			isShow: true,
			isEditMode: true,
			initialValues: action.payload
		}
	}),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar })
}, pollForm.reducers);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.pollsList.data,
	getTable: state => {
		const { table, data } = state.pollsList;
		const rows = state.pollsList.data ? sortColumns(data, table.sortByColumn) : [];
		return {
			headers: values(table.columns),
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getPagination: state => {
		const { perPage, total, currentPage } = state.pollsList.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage
		};
	},
	getPollFormInitialValues: pollForm.selectors.getInitialValues,
	getShouldShowPollForm: pollForm.selectors.getShouldShowPollForm,
	getPollFormEditMode: pollForm.selectors.getPollFormEditMode,
	getStatusesList: pollForm.selectors.getStatusesList,
	getLanguagesList: pollForm.selectors.getLanguagesList,
	getIntl: state => intl(state),
	getProgressBar: state => state.pollsList.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.pollsList.error
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(rows, row => row[sortedColumn.index], sortedColumn.direction);
};

const request = (offset = 0, limit = 50, dispatch) => {
	const query = qs.stringify({
		offset,
		limit
	});

	return http({
		url: `${endpoints.POLLS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	})(dispatch);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/polls/index.js